<!--
  PP-3635: full-screen "Processing Payment..." overlay during an in-flight Authorize/retry. Rendered via the
  CDK overlay (see PaymentInfoStepComponent.openProcessingOverlay) so it attaches at the document root and
  covers the whole viewport, including the engagement sidebar — an inline element here is trapped inside
  mat-drawer-content's stacking context and paints behind the sidebar.
-->
<ng-template #processingOverlayTemplate>
  <div class="engagements-payment-info-processing-overlay">
    <div class="engagements-payment-info-processing-overlay-content">
      <mat-spinner [diameter]="96" [strokeWidth]="8"></mat-spinner>
      <p class="engagements-payment-info-processing-overlay-message">Processing Payment...</p>
    </div>
  </div>
</ng-template>

@switch (dependenciesStatus$ | async) {
  @case (LoadableStatus.LOADED) {
    <div class="engagements-payment-info">
      <h1 class="engagements-payment-info-header">Payment Information</h1>

      @if (
        [EngagementStepStatus.COMPLETE, EngagementStepStatus.FAILED].includes(details().status)
        || (prepaymentRequired$ | async)
        || (isAutoDraftEngagement$ | async)
        || !(autoPayData$ | async)?.isEnabled
      ) {
        <ng-container *ngTemplateOutlet="paymentMethodTemplate"></ng-container>
        <ng-container *ngTemplateOutlet="prepaymentTemplate"></ng-container>
        <ng-container *ngTemplateOutlet="autoDraftTemplate"></ng-container>
        <ng-container *ngTemplateOutlet="setUpAutoPayTemplate"></ng-container>
      } @else {
        <ng-container *ngTemplateOutlet="autoPayActiveTemplate"></ng-container>
      }
    </div>
  }
  @case (LoadableStatus.ERROR) {
    <div class="engagements-payment-info-error">
      <div class="alert alert--error">Failed to load payment information step.</div>
    </div>
  }
  @default {
    <div class="engagements-payment-info-loading">
      <mat-spinner [diameter]="100"></mat-spinner>
    </div>
  }
}

<ng-template #paymentMethodTemplate>
  @if (showWaitingPaymentMethodPseudoSelector$ | async) {
    <!--
      FIRM-10915: waiting-state read-only pseudo-selector. Shows the engagement's stored method; the
      "Change payment method" trigger reveals the full picker in place. There is intentionally no cancel
      back to this view — the client either submits the swapped method or leaves the step. Wrapped in a
      single element so it is one flex child of `.engagements-payment-info` (whose `gap` would otherwise
      space the header / banner / box ~40px apart); internal spacing is the elements' own `mb-4`.
    -->
    <ng-container *ngTemplateOutlet="usedPaymentMethodPseudoSelectorTemplate; context: { showChangeButton: true }"></ng-container>
  } @else if (showPaymentMethodPicker$ | async) {
    <app-select-payment-method
      [intent]="PaymentMethodIntent.PAYMENT"
      [paymentFlow]="PaymentFlow.PLAN"
      [paymentMethodMode]="paymentMethodMode$ | async"
      [setUpLaterEnabled]="(selectedEngagement$ | async).paymentInfoType !== PaymentInfoType.REQUIRED"
      [selectedPaymentMethodId]="selectedPaymentMethodIdV5$ | async"
      [surface]="'engagement-payment-info-step'"
      (paymentMethodModeChange)="paymentMethodMode$.next($event)"
      (paymentMethodSelected)="selectedPaymentMethodV5$.next($event)"
    >
      <h2 ngProjectAs="select-payment-method-header" class="h2-500 color-gray-100 mb-4">Payment Method</h2>
      <!-- PP-3635: status banner renders directly under the Payment Method header, above the picker. -->
      <ng-container ngProjectAs="select-payment-method-header">
        <ng-container *ngTemplateOutlet="prepaymentStatusBannerTemplate"></ng-container>
      </ng-container>
      <h2 ngProjectAs="select-payment-method-subheader" class="small-300 color-gray-400 mb-4">
        {{ paymentMethodSubheader$ | async }}
      </h2>
    </app-select-payment-method>
  } @else if (isStepComplete$ | async) {
    @if (syncPrepaymentEnabled$ | async) {
      <!-- PP-3635: sync flow renders the read-only "Used payment method" pseudo-selector + status banner. -->
      <ng-container *ngTemplateOutlet="usedPaymentMethodPseudoSelectorTemplate; context: { showChangeButton: false }"></ng-container>
    } @else {
      <p class="small-300 color-gray-400">
        This step has been completed previously. The payment method and other payment details for this engagement can be seen below.
      </p>

      <div class="d-flex align-items-center justify-content-between">
        <h4 class="h4-500 color-gray-100">Saved Payment Method</h4>

        @let selectedPaymentMethod = pseudoV5PaymentMethod$ | async;
        @if (selectedPaymentMethod) {
          <div class="engagements-payment-info-complete-payment-method-display tc--darkest">
            <img [src]="selectedPaymentMethod.icon" width="24" height="24" alt="">
            •••• •••• •••• {{ selectedPaymentMethod.lastFour }}
          </div>
        } @else {
          <p class="body-300 color-gray-100">None provided</p>
        }
      </div>
    }
  }
</ng-template>

<!--
  PP-3635: read-only "Used payment method" pseudo-selector — the Payment Method header, the prepayment
  status banner, and the engagement's stored method. Shared by the awaiting-signatures state (with the
  "Change payment method" trigger) and the completed sync state (read-only, no trigger).
-->
<ng-template #usedPaymentMethodPseudoSelectorTemplate let-showChangeButton="showChangeButton">
  <div>
    <h2 class="h2-500 color-gray-100 mb-4">Payment Method</h2>
    <ng-container *ngTemplateOutlet="prepaymentStatusBannerTemplate"></ng-container>

    <div class="payment-method-mode-container payment-method-mode-container-selected engagements-payment-info-used-payment-method">
      <div class="engagements-payment-info-used-payment-method-details">
        <label class="body-600 color-gray-100">Used payment method</label>

        @let usedPaymentMethod = pseudoV5PaymentMethod$ | async;
        @if (usedPaymentMethod) {
          <div class="engagements-payment-info-used-payment-method-display tc--darkest">
            <img [src]="usedPaymentMethod.icon" width="24" height="24" alt="">
            {{ usedPaymentMethod.name }} •••• {{ usedPaymentMethod.lastFour }}
          </div>
        } @else {
          <p class="body-300 color-gray-100">None provided</p>
        }
      </div>

      @if (showChangeButton) {
        <button
          type="button"
          class="engagements-payment-info-change-payment-method-trigger"
          (click)="isChangingPaymentMethod$.next(true)"
          data-cy="change-payment-method-button"
        >
          Change payment method
        </button>
      }
    </div>
  </div>
</ng-template>

<!--
  PP-3635: sync-prepayment status banner. Rendered under the Payment Method header (in both the picker and
  read-only branches above). Gated by the sync flag, so nothing renders when sync prepayment is off.
-->
<ng-template #prepaymentStatusBannerTemplate>
  @if (syncPrepaymentEnabled$ | async) {
    @switch (prepaymentStepState$ | async) {
      @case ('failed') {
        <div class="alert alert--error engagements-payment-info-prepayment-failed mb-4">
          @if (paymentMethodUpdatedAfterFailure$ | async) {
            <!--
              PP-3851: flagged, deliberately NOT changed. "Has been updated" is past tense and sounds
              persisted, but nothing is saved against the engagement until Try Again runs the charge. This is
              design's approved copy and is in the Figma frame, so amending it is a design conversation.
            -->
            <p>Payment method has been updated. Select <strong>{{ submitButtonLabel() || 'Try Again' }}</strong> to reprocess the payment.</p>
          } @else {
            <!--
              PP-3851 Part 2 (ACs 7-11): the decline lead plus guidance keyed on anduin's `retryGuidance`
              (ART-8832). THE FOUR SENTENCES IN THE SWITCH BELOW LIVE ONLY HERE. All of this copy is a
              proposal pending design sign-off, and per the 2026-08-21 decision Stephen owns the three
              classified strings. @default is the pre-PP-3851 sentence and catches an absent or unrecognised
              value, so the banner degrades to what shipped rather than blanking if this reaches production
              ahead of the backend. The throttle sentence is the one piece of this copy held in the component
              instead, because that terminal's snackbar reuses it and the two must not drift.
            -->
            <p>
              {{ prepaymentFailureMessage$ | async }}
              @if (isPrepaymentRetryThrottled$ | async) {
                <!--
                  PP-3851: the attempt was throttled, so no charge ran. The lead above still names the prior
                  decline, which is still what the client has to fix, but every classified sentence below
                  invites an action they cannot take right now, so the switch is suppressed entirely.
                  This string lives in the component, since the snackbar for this terminal reuses it.
                -->
                {{ prepaymentRetryThrottledGuidance }}
              } @else {
                @switch (prepaymentRetryGuidance$ | async) {
                  @case ('RETRY_NOW') {
                    Select <strong>{{ submitButtonLabel() || 'Try Again' }}</strong> to retry with the payment method below. Change it first if you want to use a different one.
                  }
                  @case ('RETRY_AFTER_ISSUER') {
                    Contact your card issuer, then select <strong>{{ submitButtonLabel() || 'Try Again' }}</strong>. To use a different payment method, change it below first.
                  }
                  @case ('CHANGE_METHOD') {
                    Choose a different payment method below, then select <strong>{{ submitButtonLabel() || 'Try Again' }}</strong>.
                  }
                  @default {
                    You may try again and change the payment method below.
                  }
                }
              }
            </p>
          }
        </div>
      }
      @case ('processing') {
        <div class="alert engagements-payment-info-prepayment-processing mb-4">
          <p>Payment is currently processing and may take up to 3 days to be fully processed. You'll be notified by email when processing is completed.</p>
        </div>
      }
      @case ('waiting') {
        <!-- PP-3635: payment confirmed, charge deferred until all signatures are collected. Copy pluralizes by outstanding-signature count. -->
        <div class="alert engagements-payment-info-prepayment-waiting mb-4">
          <p>{{ awaitingSignaturesMessage$ | async }}</p>
        </div>
      }
      @case ('completed') {
        <div class="alert alert--success engagements-payment-info-prepayment-completed mb-4">
          <p>This step has been completed previously and the payment method and other payment details for this engagement can be seen below.</p>
        </div>
      }
    }
  }
</ng-template>

<ng-template #prepaymentTemplate>
  @if ((prepaymentRequired$ | async) || (isStepComplete$ | async)) {
    <div>
      <div>
        <div class="d-flex align-items-baseline justify-content-between mb-4">
          @if ((isAutoDraftEngagement$ | async) && !(isStepStrictlyComplete$ | async)) {
            <h2 class="h2-500 color-gray-100">Due Now</h2>
            <h4 class="h4-500 color-gray-400">Prepayment</h4>
          } @else {
            <h4 class="h4-500 color-gray-100">Prepayment Summary</h4>
          }
        </div>

        @if (syncPrepaymentEnabled$ | async) {
          <!-- PP-3635: the status banner moved under the Payment Method header; the prepayment section keeps the standard description. -->
          <p class="small-300 color-gray-400 mb-6">{{ prepaymentMessage$ | async }}</p>
        } @else if (didPrepaymentFail$ | async) {
          @let prepayment = prepayment$ | async;
          <div class="alert alert--error engagements-payment-info-prepayment-failed-legacy mb-6">
            <p>Payment failed on {{ prepayment.allocationDate | date }}</p>
            <button (click)="navigateToReviewAndPayEngagementPrepayment(prepayment.arTranId)">Pay Now</button>
          </div>
        } @else {
          <p class="small-300 color-gray-400 mb-6">{{ prepaymentMessage$ | async }}</p>
        }
      </div>

      <div class="d-flex flex-column gap-6 bg-color-gray-1100 rounded-all-1 p-4">
        @if (!(isEngagementExecuted$ | async)) {
          <h4 class="color-gray-100">Amount charged when engagement is executed</h4>
        }

        @if (prepaymentRequired$ | async) {
          @for (prepaymentJob of prepaymentJobs$ | async; track prepaymentJob.id) {
            <div class="engagements-payment-info-prepayment-job">
              <p class="body-400 color-gray-300">{{ prepaymentJob.name }}</p>
              <h4 class="h4-500 color-gray-100">
                {{ prepaymentJob.prepaymentAmount | currency : (clientCurrency$ | async).code }}
              </h4>
            </div>
          }

          <hr class="mt-0">
        }


        <app-payment-summary
          [overriddenSelectedPaymentMethod$]="pseudoV5PaymentMethod$"
          [overriddenSubtotal$]="prepaymentSubtotal$"
          [forceZeroState]="(prepaymentSubtotal$ | async) === 0"
          [ignoreLateFees]="true"
          [currency]="clientCurrency$ | async"
          [overriddenBusinessEntityId]="paymentPlatformService.getSelectedBusinessEntityId() | async"
        ></app-payment-summary>
      </div>
    </div>
  }
</ng-template>

<ng-template #autoDraftTemplate>
  @if (isAutoDraftEngagement$ | async) {
    <div>
      <div class="d-flex align-items-baseline justify-content-between mb-4">
        @if ((prepaymentRequired$ | async) && !(isStepStrictlyComplete$ | async)) {
          <h2 class="h2-500 color-gray-100">Due Later</h2>
          <h4 class="h4-500 color-gray-400">Future Installments</h4>
        } @else {
          <h4 class="h4-500 color-gray-100">Future Installments</h4>
        }
      </div>

      <p class="small-300 color-gray-400">
        All Projects under this engagement will be automatically paid using the payment method
        @if (isStepStrictlyComplete$ | async) {
          above. All scheduled payments can be seen below:
        } @else {
          @let prepaymentJobs = prepaymentJobs$ | async;
          provided above. Please review {{ prepaymentJobs.length > 1 ? 'all upcoming billing schedules' : 'the upcoming billing schedule' }}
          outlined below:
        }
      </p>

      <mat-accordion class="engagements-payment-info-auto-draft-breakdown">
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <div>
              <h3 class="h3-500 color-gray-100">
                {{ (autoDraftJobs$ | async)?.length }} project{{ (autoDraftJobs$ | async)?.length === 1 ? '' : 's' }}
              </h3>
              <p class="small-300 color-gray-300">{{ autoDraftRange$ | async }}</p>
            </div>
          </mat-expansion-panel-header>

          <hr class="mt-3">

          <div class="d-flex align-items-center justify-content-between">
            @let installmentsCount = autoDraftInstallmentsCount$ | async;
            <p class="small-300 color-gray-300">{{ installmentsCount }} payment installment{{ installmentsCount === 1 ? '' : 's' }}</p>
            <h4 class="h4-500 color-gray-100">
              {{ autoDraftTotal$ | async | currency : (clientCurrency$ | async).code }}
            </h4>
          </div>

          @for (autoDraftJob of autoDraftJobs$ | async; let index = $index; track autoDraftJob.id) {
            <hr>

            <div class="d-flex flex-column gap-4">
              <!-- Header -->
              <div>
                <div class="d-flex justify-content-between">
                  <h4 class="h4-500 color-gray-100">{{ index + 1 }}- {{ autoDraftJob.name }}</h4>
                  <h4 class="h4-500 color-gray-100">{{ autoDraftJob.total | currency : (clientCurrency$ | async).code }}</h4>
                </div>

                <p class="small-300 color-gray-300">
                  {{ autoDraftJob.billingScheduleType | titlecase }} billing schedule
                </p>
              </div>

              <!-- Installments -->
              @for (schedule of autoDraftJob.schedule; track schedule) {
                <div>
                  <div class="d-flex justify-content-between">
                    <p class="body-300 color-gray-100">{{ schedule.label }}</p>
                    <p class="body-400 color-gray-300">
                      {{ schedule.installmentsCount }}x {{ schedule.amountPerInstallment | currency : (clientCurrency$ | async).code }}
                    </p>
                  </div>

                  <p class="small-300 color-gray-300">
                    {{ schedule.installmentsCount }} payment installment{{ schedule.installmentsCount === 1 ? '' : 's' }}
                  </p>
                </div>
              }
            </div>
          }
        </mat-expansion-panel>
      </mat-accordion>
    </div>

    @if ((canProvidePaymentInfo$ | async) && !(isStepStrictlyComplete$ | async)) {
      <mat-accordion class="engagements-payment-info-auto-draft-agreement">
        <mat-expansion-panel>
          <mat-expansion-panel-header>
            <div>
              <p class="info-block-title">Scheduled Payment Agreement</p>
              <p class="info-block-subtitle">By clicking "Authorize & Complete," you agree to the terms below.</p>
            </div>
          </mat-expansion-panel-header>

          <p class="small-300 color-blue-400">
            I confirm that I have reviewed the billing details above and consent to {{ configService.customerName }}
            charging the payment method I provided, based on the timing and amounts shown.
            <br><br>
            I understand that I will receive an email notice at least 48 hours before each scheduled payment. If I wish to update the billing
            schedule or cancel a payment, I must contact {{ configService.customerName }} before the scheduled charge date.
            <br><br>
            If a payment attempt fails, the corresponding invoice will appear in the client portal as unpaid.
            I am responsible for logging in and manually submitting payment by the due date listed on the invoice.
          </p>
        </mat-expansion-panel>
      </mat-accordion>
    }
  }
</ng-template>

<ng-template #setUpAutoPayTemplate>
  @if (
    autoPayConfigsEnabled
    && (autoPayData$ | async)?.isEnabled === false
    && !(autoPayData$ | async)?.isClientNonUsd
    && !(isAutoDraftEngagement$ | async)
  ) {
    <div class="engagements-payment-info-autopay-setup">
      <p>Did you know you can set up AutoPay for future invoices? Start saving time today!</p>
      <button (click)="manageAutoPay()" class="engagements-payment-info-autopay-setup-trigger mt--050">Set up AutoPay</button>
    </div>
  }
</ng-template>

<ng-template #autoPayActiveTemplate>
  <p>
    {{ (selectedEngagement$ | async).clientName }} is already enrolled in AutoPay for future invoices.
    Please verify the following information.
  </p>

  @let autoPayData = autoPayData$ | async;
  @if (autoPayData) {
    <div class="engagements-payment-info-autopay-manage">
      <p>
        AutoPay is scheduled for the {{ autoPayData.autoPay.day }} of each month using
        {{ autoPayData.autoPay.method.cardType | uppercase }} ••••{{ autoPayData.autoPay.method.last4 }}.
        @if (autoPayData.autoPay.amount) {
          <em class="color-red-50">
            Please note that your AutoPay limit is set to {{ autoPayData.autoPay.amount | currency }} each month.
          </em>
        }
      </p>

      <button (click)="manageAutoPay()" class="engagements-payment-info-autopay-manage-trigger mt--050">Manage AutoPay...</button>
    </div>
  }
</ng-template>
